import _ from 'lodash';
import {fitToRange} from 'lib/numericHelper';
import {cloneAndMapObject, find} from 'lib/objectHelper';
import {CopyPipettingValueMode, PipettingParameterKeysByTaskType} from 'constants/cardStateEnum';
import {setTipXPositionOffset, setTipYPositionOffset} from 'stores/actions/cardStateAction';
import {calculateTipPositionOffset, omitTipDirectionalParameters} from 'lib/PipettingProfileItemHelper';
import {CommonButtonLabel, DirectionType, TaskType, TrayType} from 'constants/dataEnum';
import {PIPETTING_PARAMETERS_TIP_DIRECTIONAL_SETTINGS} from 'constants/pipettingParameterEnum';
import {parseMessage} from 'lib/stringHelper';
import {isSameLabwareHash} from 'contents/ProtocolEditor/VirtualTray/labwareHelper';
// eslint-disable-next-line import/no-cycle
import {getMaxTipPositionByLabwareId, getMinTipPositionByLabwareId} from './protocolManagementBusiness';
import {showModalSimplified} from './commonModalBusiness';

export const extractPipettingParameterFromTask = (task, isIncludeDirectionalTipSettings = true) => {
  const pipettingParameterFieldKeysBasedOnTaskType = PipettingParameterKeysByTaskType[task.type];

  const pipettingParameterFieldKeys = isIncludeDirectionalTipSettings
    ? pipettingParameterFieldKeysBasedOnTaskType
    : pipettingParameterFieldKeysBasedOnTaskType.filter(
        (param) => !PIPETTING_PARAMETERS_TIP_DIRECTIONAL_SETTINGS.includes(param),
      );

  return cloneAndMapObject(task, pipettingParameterFieldKeys);
};

// TODO: create a factory that generates default tasks
// export const generateNewDefaultTask = (taskType) => {

// };

export const adjustTaskPipettingParametersByLabware = (task, labware) => (dispatch) => {
  const minTipPosition = dispatch(getMinTipPositionByLabwareId(labware.LabwareID, labware));
  const maxTipPosition = dispatch(getMaxTipPositionByLabwareId(labware.LabwareID));

  const getAdjustedTipPosition = (val) => fitToRange(val, minTipPosition, maxTipPosition);
  const newTask = _.cloneDeep(task);

  return {
    ...newTask,
    tipPosition: getAdjustedTipPosition(newTask.tipPosition),
    mixingPositionAspirate: getAdjustedTipPosition(newTask.mixingPositionAspirate),
    mixingPositionDispense: getAdjustedTipPosition(newTask.mixingPositionDispense),
    tipTouchOffHeight: getAdjustedTipPosition(newTask.tipTouchOffHeight),
  };
};

export const updatePipettingTipPositionOffset =
  (direction, value, task, onlySaveValue = false) =>
  (dispatch, getState) => {
    const {labwareState} = getState();
    const {ID: taskId, labwareID, tipXPositionOffset, tipYPositionOffset} = task || {};

    // to save the current direction
    if (direction === DirectionType.DIRECTION_X) {
      dispatch(setTipXPositionOffset(taskId, value));
    } else {
      dispatch(setTipYPositionOffset(taskId, value));
    }

    if (onlySaveValue) return;
    if (direction === DirectionType.DIRECTION_X && tipYPositionOffset === 0) return;
    if (direction === DirectionType.DIRECTION_Y && tipXPositionOffset === 0) return;

    // to check and set the other direction
    const labware = find(labwareState, {LabwareID: labwareID});
    if (labware?.isWellRectangle) return;
    const {xOffset, yOffset} = calculateTipPositionOffset(
      direction,
      value,
      tipYPositionOffset,
      tipXPositionOffset,
      labware,
    );

    if (direction === DirectionType.DIRECTION_X) {
      dispatch(setTipYPositionOffset(taskId, yOffset));
    } else {
      dispatch(setTipXPositionOffset(taskId, xOffset));
    }
  };

export const getRangeSpeedPipettingParameter = (task) => (dispatch, getState) => {
  const {protocolState, configState} = getState();

  const {lastOpenedProtocol} = protocolState;
  const {trayType} = lastOpenedProtocol;

  const {rangeAspirateSpeed, rangeAspirateSpeedHV, rangeDispenseSpeed, rangeDispenseSpeedHV} = configState;
  const {type} = task;

  const rangeSpeed = type === TaskType.ASPIRATE ? rangeAspirateSpeed : rangeDispenseSpeed;
  const rangeSpeedHV = type === TaskType.ASPIRATE ? rangeAspirateSpeedHV : rangeDispenseSpeedHV;

  return trayType === TrayType.HIGH_VOLUME_CADDY ? rangeSpeedHV : rangeSpeed;
};

export const getRangeMixingSpeedPipettingParameter = (task) => (dispatch, getState) => {
  const {protocolState, configState} = getState();

  const {lastOpenedProtocol} = protocolState;
  const {trayType} = lastOpenedProtocol;

  const {
    rangeMixingBeforeAspirateSpeed,
    rangeMixingBeforeAspirateSpeedHV,
    rangeMixingAfterDispenseSpeed,
    rangeMixingAfterDispenseSpeedHV,
  } = configState;
  const {type} = task;

  const rangeMixingSpeed = () => {
    if (type === TaskType.MIXING) return rangeMixingAfterDispenseSpeed;

    return type === TaskType.ASPIRATE ? rangeMixingBeforeAspirateSpeed : rangeMixingAfterDispenseSpeed;
  };

  const rangeMixingSpeedHV = () => {
    if (type === TaskType.MIXING) return rangeMixingAfterDispenseSpeedHV;

    return type === TaskType.ASPIRATE ? rangeMixingBeforeAspirateSpeedHV : rangeMixingAfterDispenseSpeedHV;
  };

  return trayType === TrayType.HIGH_VOLUME_CADDY ? rangeMixingSpeedHV() : rangeMixingSpeed();
};

export const showApplyPipettingConfirmationModal = (copyPipettingMode, callback) => (dispatch) => {
  let applyScopeLabel = '';

  switch (copyPipettingMode) {
    case CopyPipettingValueMode.TO_ALL_TASKS:
      applyScopeLabel = parseMessage('modal.protocolEditor.applyPipettingParameters.text.scopeMultipleTasks');
      break;
    case CopyPipettingValueMode.TO_ALL_CARDS:
      applyScopeLabel = parseMessage(
        'modal.protocolEditor.applyPipettingParameters.text.scopeMultipleTasksAcrossCards',
      );
      break;
    case CopyPipettingValueMode.TO_SINGLE_TASK:
      applyScopeLabel = parseMessage('modal.protocolEditor.applyPipettingParameters.text.scopeSingularTask');
      break;
    case CopyPipettingValueMode.TO_SINGLE_CARD:
      applyScopeLabel = parseMessage('modal.protocolEditor.applyPipettingParameters.text.scopeSingularCard');
      break;
    default:
      break;
  }

  const title = parseMessage('modal.protocolEditor.applyPipettingParameters.title');
  const body = parseMessage('modal.protocolEditor.applyPipettingParameters.text', {
    applyScope: applyScopeLabel,
  });

  const modalButtons = [
    {
      onClick: callback,
      text: parseMessage(CommonButtonLabel.CONFIRM),
    },
    {
      text: parseMessage(CommonButtonLabel.CANCEL),
    },
  ];

  dispatch(showModalSimplified(title, body, modalButtons));
};

export const getAppliedPipettingParameters =
  (taskSource = {pipettingParameters: {}, labware: {}}, taskDestination = {pipettingParameters: {}, labware: {}}) =>
  (dispatch) => {
    const {pipettingParameters: sourcePipettingParameters, labware: sourceLabware} = taskSource;
    const {pipettingParameters: destPipettingParameters, labware: destLabware} = taskDestination;

    const sourceLabwareToCompare = sourceLabware.labwareIndex
      ? sourceLabware.labwareIndexes[sourceLabware.labwareIndex]
      : sourceLabware;
    const destLabwareToCompare = destLabware.labwareIndex
      ? destLabware.labwareIndexes[destLabware.labwareIndex]
      : destLabware;

    const isLabwareSameType = isSameLabwareHash(sourceLabwareToCompare, destLabwareToCompare);
    let pipettingParameterToApply = sourcePipettingParameters;

    if (!isLabwareSameType) {
      pipettingParameterToApply = omitTipDirectionalParameters(sourcePipettingParameters);
    }

    const adjustedPipettingParam = dispatch(
      adjustTaskPipettingParametersByLabware(pipettingParameterToApply, destLabware),
    );

    return {...destPipettingParameters, ...adjustedPipettingParam};
  };
